import * as React from 'react';
import { Line, Html } from '@react-three/drei';
import * as THREE from 'three';

type _Props = {
  color?: string;
  end: [number, number, number];
  lineWidth?: number;
  relationshipValue?: string;
  start: [number, number, number];
};

const ThreeJSConnection = ({ color = '#1890ff', end, lineWidth = 2, relationshipValue, start }: _Props) => {
  const [hovered, setHovered] = React.useState(false);

  // Create points for the curved line
  const points = React.useMemo(() => {
    const curve = new THREE.CubicBezierCurve3(
      new THREE.Vector3(...start),
      new THREE.Vector3(start[0], start[1], start[2] + 0.5),
      new THREE.Vector3(end[0], end[1], end[2] + 0.5),
      new THREE.Vector3(...end)
    );
    return curve.getPoints(20);
  }, [start, end]);

  // Calculate midpoint for tooltip position
  const midpoint = React.useMemo(() => {
    const mid = points[Math.floor(points.length / 2)];
    return [mid.x, mid.y, mid.z];
  }, [points]);

  const colors = React.useMemo(() => {
    const startColor = new THREE.Color(color);

    if (hovered) {
      return points.map(() => new THREE.Color('#ff4d4f'));
    }

    return points.map((_, index) => {
      const t = index / (points.length - 1);

      if (t < 0.15 || t > 0.85) {
        return new THREE.Color('#ff4d4f');
      }
      return startColor;
    });
  }, [points, color, hovered]);

  return (
    <group>
      <Line
        lineWidth={hovered ? lineWidth * 1.5 : lineWidth}
        onPointerOut={() => setHovered(false)}
        onPointerOver={() => setHovered(true)}
        points={points}
        vertexColors={colors}
      />

      {hovered && relationshipValue && (
        <Html position={midpoint as [number, number, number]}>
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {relationshipValue}
          </div>
        </Html>
      )}
    </group>
  );
};

export default ThreeJSConnection;
